"use client"

import * as React from "react"
import { useTranslations } from "@/i18n"
import { type ColumnDef, type FilterFn } from "@tanstack/react-table"
import { BanIcon, InfoIcon, MoreHorizontalIcon, PencilIcon, RotateCcwIcon } from "lucide-react"

import { DataTable, DataTableProgressiveListCell, DataTableVariant } from "@ibada/ui/components/data-table"
import type { DataTableToolbarActionInput } from "@ibada/ui/components/data-table"
import { useTableQuery } from "@/shared/lib/data-table/table-query"
import { dataTableOverflowActionsColumnSizing } from "@/shared/lib/data-table/data-table-overflow-actions-column"
import { UserAvatar } from "@ibada/ui"
import { Badge } from "@ibada/ui"
import { Button, Tooltip, TooltipContent, TooltipTrigger } from "@ibada/ui"
import { AliveActionsCell, AliveLeadCell, AlivePillTone, AliveStatusCell } from "@/shared/components/alive-cells"
import { getStatusToneClassName } from "@ibada/ui/lib/status-tone"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@ibada/ui"
import { UserBranchAccessStatus, WorkspaceUserStatus } from "@/features/auth/model/auth-types"
import { permissionScopeLabelForBranchType } from "@/features/users/lib/permission-branch-ui"
import { type WorkspaceTeamMember } from "@/features/users/model"
import { PersonPreview } from "@/shared/components/person-preview"
import { getBranchDisplayLabel } from "@/shared/lib/branch/branch-display"
import { useDataTableToolbarConfig } from "@/shared/lib/data-table/use-data-table-toolbar-config"
import { useIsMobile } from "@/shared/hooks/use-mobile"
import { useMobileListChrome } from "@/shared/components/mobile-list-chrome"

export enum UserRowAction {
  EDIT_ACCESS = "edit_access",
  VIEW_ACCESS = "view_access",
  /**
   * Pause someone without dismantling what they have.
   *
   * Suspending is a status change on the workspace user and nothing else — roles, branch access and module
   * permissions stay exactly as configured, so restoring is one flip back rather than a rebuild from
   * memory. Authorization already honours it: every guard resolves through a lookup that requires an
   * ACTIVE workspace user, so a suspended person is refused everywhere.
   */
  SUSPEND = "suspend",
  RESTORE = "restore",
}

/** Optional segmented control rendered inside the table toolbar (e.g. Users / Invitations). */
export type TableToolbarViews = {
  value: string
  onValueChange: (value: string) => void
  /** `count` is shown beside the label where the host has it (the desktop team panel's pills). */
  items: Array<{ value: string; label: string; count?: number }>
}

type UsersTableProps = {
  users: WorkspaceTeamMember[]
  isLoading?: boolean
  canManage: boolean
  canManageRow: (user: WorkspaceTeamMember) => boolean
  /**
   * Is this row the signed-in user?
   *
   * Suspending yourself locks you out of the workspace you are administering — and if you are its only OWNER,
   * out of the only account that could undo it. The row still offers everything else; only the door you are
   * standing in is not yours to close.
   */
  isSelf: (user: WorkspaceTeamMember) => boolean
  onRefresh?: () => Promise<unknown> | void
  isRefreshing?: boolean
  onAction: (action: UserRowAction, user: WorkspaceTeamMember) => void
  toolbarActions?: DataTableToolbarActionInput[]
  /** When set, a segmented control (e.g. Users/Invitations) renders in the toolbar. */
  toolbarViews?: TableToolbarViews
}

function getUserDisplayName(user: WorkspaceTeamMember, fallback: string) {
  const fullName = `${user.firstName} ${user.lastName}`.trim()
  if (fullName.length > 0) return fullName
  if (user.member) {
    const memberName = `${user.member.firstName} ${user.member.lastName}`.trim()
    if (memberName.length > 0) return memberName
  }
  return fallback
}


/**
 * The branches someone can see, each carrying the id of the access row that grants it.
 *
 * The id has to come from the row, never from the label. `Branch.name` has no uniqueness constraint —
 * only slug and code do, and creating a second "Finance" branch just auto-suffixes its slug — and every
 * branch-less access row renders as the same "unknown branch" fallback. So labels collide in ordinary use,
 * and keying a list by them drops entries: the "+n" would promise more than the popover shows.
 */
export function getActiveDashboardBranchItems(
  user: WorkspaceTeamMember,
  t: (key: string, values?: Record<string, string | number>) => string
): Array<{ id: string; label: string }> {
  const rows = user.userBranchAccesses ?? []
  return rows
    .filter((row) => row.status === UserBranchAccessStatus.ACTIVE)
    .map((row) => ({
      id: row.id,
      label: permissionScopeLabelForBranchType(
        row.branch?.branchType,
        row.branch?.name ?? "",
        (key) => (key === "parish" ? t("scope.parish") : t("unknownBranch"))
      ),
    }))
    .filter((item) => item.label.length > 0)
}

function getActiveDashboardBranchNames(
  user: WorkspaceTeamMember,
  t: (key: string, values?: Record<string, string | number>) => string
): string[] {
  return getActiveDashboardBranchItems(user, t).map((item) => item.label)
}

function formatDashboardBranchSummary(
  user: WorkspaceTeamMember,
  t: (key: string, values?: Record<string, string | number>) => string
) {
  const names = getActiveDashboardBranchNames(user, t)
  if (names.length === 0) {
    return t("branchAccessSummary.none")
  }
  if (names.length === 1) return t("branchAccessSummary.one", { name: names[0]! })
  if (names.length === 2) {
    return t("branchAccessSummary.two", { a: names[0]!, b: names[1]! })
  }
  return t("branchAccessSummary.many", {
    a: names[0]!,
    b: names[1]!,
    count: names.length - 2,
  })
}

export function UsersTable({
  users,
  isLoading = false,
  canManage,
  canManageRow,
  isSelf,
  onRefresh,
  isRefreshing = false,
  onAction,
  toolbarActions,
  toolbarViews,
}: UsersTableProps) {
  const t = useTranslations("users.table")
  const tCommon = useTranslations("common")
  const isMobile = useIsMobile()
  const { queryState, filterConfig } = useTableQuery({
    config: {
      id: "users-table",
      search: {
        placeholderKey: "searchPlaceholder",
        className: "w-full max-w-sm",
      },
    },
    t,
  })
  const globalFilterFn = React.useMemo<FilterFn<WorkspaceTeamMember>>(
    () => (row, _columnId, filterValue) => {
      const query = String(filterValue ?? "").trim().toLowerCase()
      if (!query) return true

      const user = row.original
      const displayName = getUserDisplayName(user, t("unknownTeamMember")).toLowerCase()
      const email = user.email.toLowerCase()
      const memberBranch = (user.member?.branch?.name ?? "").toLowerCase()
      const dashboardBranches = getActiveDashboardBranchNames(user, t)
        .join(" ")
        .toLowerCase()
      const permissionTargetSummary = formatDashboardBranchSummary(user, t).toLowerCase()
      return (
        displayName.includes(query) ||
        email.includes(query) ||
        memberBranch.includes(query) ||
        dashboardBranches.includes(query) ||
        permissionTargetSummary.includes(query)
      )
    },
    [t]
  )

  const columns = React.useMemo<ColumnDef<WorkspaceTeamMember>[]>(
    () => [
      {
        id: "name",
        header: t("columns.name"),
        /*
         * The name column had no accessor, so it carried no sortable value and the table fell back to
         * sorting by email. A list of people headed by Name that is actually ordered by the hidden local
         * part of their address reads as unsorted, because it is — by anything the reader can see.
         */
        accessorFn: (member) => getUserDisplayName(member, t("unknownTeamMember")),
        /*
         * Collated, not compared byte by byte.
         *
         * Every built-in sorting function — `text` included — ultimately does `a > b` on the raw string,
         * which is UTF-16 code-unit order. In a French congregation that puts Émile and Çirimwami after
         * Zacharie, which is exactly the "order the reader cannot see" this column was fixed to avoid.
         * `localeCompare` at base sensitivity files accents where a reader expects them.
         */
        sortingFn: (rowA, rowB, columnId) =>
          String(rowA.getValue(columnId)).localeCompare(String(rowB.getValue(columnId)), undefined, {
            sensitivity: "base",
          }),
        size: isMobile ? 200 : 300,
        minSize: isMobile ? 168 : 240,
        maxSize: 420,
        meta: { cellClassName: "max-w-0 overflow-hidden" },
        cell: ({ row }) => {
          const label = getUserDisplayName(row.original, t("unknownTeamMember"))
          const avatarUrl = row.original.avatarUrl ?? row.original.member?.avatarUrl ?? null
          return (
            <PersonPreview
              variant='hover'
              identity={{
                memberId: row.original.member?.id ?? null,
                userId: row.original.id,
                email: row.original.email,
              }}
              fallback={{ name: label, avatarUrl }}
            >
              {isMobile ? (
                <div className='flex min-w-0 items-center gap-2.5'>
                  <UserAvatar className='shrink-0' name={label} src={avatarUrl} />
                  <span className='block min-w-0 truncate font-medium' title={label}>
                    {label}
                  </span>
                </div>
              ) : (
                // Desktop: the email rides under the name instead of a column of its own.
                <AliveLeadCell
                  leading={<UserAvatar className='shrink-0' name={label} src={avatarUrl} />}
                  title={<span title={label}>{label}</span>}
                  subtitle={<span title={row.original.email}>{row.original.email}</span>}
                />
              )}
            </PersonPreview>
          )
        },
      },
      // Phone keeps the email as its own line; desktop folds it under the name.
      ...(isMobile
        ? ([
      {
        accessorKey: "email",
        header: t("columns.email"),
        size: 300,
        minSize: 260,
        maxSize: 440,
        meta: { cellClassName: "max-w-0 overflow-hidden" },
        cell: ({ row }) => {
          const email = row.original.email
          return (
            <span className='block min-w-0 truncate text-sm' title={email}>
              {email}
            </span>
          )
        },
      },
          ] as ColumnDef<WorkspaceTeamMember>[])
        : []),
      {
        id: "role",
        header: t("columns.role"),
        size: 132,
        minSize: 112,
        meta: { cellClassName: "whitespace-nowrap" },
        cell: ({ row }) =>
          isMobile ? (
            <Badge variant='outline' className='!bg-transparent shadow-none'>{t(`roles.${row.original.workspaceRole}`)}</Badge>
          ) : (
            <AliveStatusCell tone={AlivePillTone.Neutral}>{t(`roles.${row.original.workspaceRole}`)}</AliveStatusCell>
          ),
      },
      {
        id: "scopeSummary",
        // The "edited in Edit access" note is said once, on the header, not repeated (and cut off) on every row.
        header: () => (
          <span className='inline-flex items-center gap-1'>
            {t("columns.scopeSummary")}
            <Tooltip>
              <TooltipTrigger asChild>
                <button type='button' className='text-muted-foreground hover:text-foreground' aria-label={t("scopeSummary.legacyModulesHint")}>
                  <InfoIcon className='size-3.5' />
                </button>
              </TooltipTrigger>
              <TooltipContent side='top' className='max-w-xs whitespace-normal'>
                {t("scopeSummary.legacyModulesHint")}
              </TooltipContent>
            </Tooltip>
          </span>
        ),
        size: 280,
        minSize: 240,
        maxSize: 420,
        meta: { cellClassName: "max-w-0 overflow-hidden" },
        /*
         * The branches themselves, not a sentence about them.
         *
         * This rendered "A, B and 3 others" — the other three were named nowhere and reachable by nothing,
         * so the only way to learn who someone could actually see was to open their access dialog. The
         * shared progressive cell shows the first two and puts the rest behind a "+n" you can click, the
         * same control the transactions table uses for fund allocations.
         */
        cell: ({ row }) => {
          const user = row.original
          const branchItems = getActiveDashboardBranchItems(user, t)

          if (branchItems.length === 0) {
            // No branch access at all: say whether permissions exist without one, or nothing does.
            const summary = formatDashboardBranchSummary(user, t)
            return (
              <span className='text-muted-foreground block min-w-0 truncate text-sm' title={summary}>
                {summary}
              </span>
            )
          }

          return (
            <DataTableProgressiveListCell
              title={t("columns.scopeSummary")}
              items={branchItems}
              maxVisible={2}
              className='text-sm'
              popoverClassName='w-56'
            />
          )
        },
      },
      {
        id: "branch",
        header: t("columns.branch"),
        meta: { defaultHidden: true },
        size: 160,
        minSize: 120,
        maxSize: 280,
        cell: ({ row }) => {
          const branchName = getBranchDisplayLabel({
            branchType: row.original.member?.branch?.branchType,
            branchName: row.original.member?.branch?.name,
            parishLabel: tCommon("scope.parish"),
            fallbackLabel: t("unknownBranch"),
          })
          return (
            <span className='block min-w-0 truncate text-sm' title={branchName}>
              {branchName}
            </span>
          )
        },
      },
      // Desktop: "View access" is the row's primary pill in the actions cell.
      ...(isMobile
        ? ([
      {
        id: "accessSummary",
        header: t("columns.accessSummary"),
        cell: ({ row }) => {
          return (
            <Button
              type='button'
              size='sm'
              variant='outline'
              onClick={() => onAction(UserRowAction.VIEW_ACCESS, row.original)}
            >
              {t("accessSummary.manageAccess")}
            </Button>
          )
        },
      },
          ] as ColumnDef<WorkspaceTeamMember>[])
        : []),
      {
        accessorKey: "status",
        header: t("columns.status"),
        cell: ({ row }) => (
          <Badge variant='secondary' className={getStatusToneClassName(row.original.status)}>
            {t(`status.${row.original.status}`)}
          </Badge>
        ),
      },
      {
        id: "actions",
        ...dataTableOverflowActionsColumnSizing,
        // Wide enough for the "View access" pill plus the ⋯ menu, so the pinned column is never cut off.
        ...(isMobile ? {} : { size: 200, minSize: 184, maxSize: 240 }),
        header: () => <div className='text-right'>{t("columns.actions")}</div>,
        cell: ({ row }) => {
          const rowCanManage = canManage && canManageRow(row.original)
          if (!isMobile) {
            const user = row.original
            return (
              <AliveActionsCell
                primary={{ label: t("accessSummary.manageAccess"), onSelect: () => onAction(UserRowAction.VIEW_ACCESS, user) }}
                groups={
                  rowCanManage
                    ? [
                        [{ id: "edit", label: t("actions.editAccess"), icon: PencilIcon, onSelect: () => onAction(UserRowAction.EDIT_ACCESS, user) }],
                        user.status === WorkspaceUserStatus.Suspended
                          ? [{ id: "restore", label: t("actions.restore"), icon: RotateCcwIcon, onSelect: () => onAction(UserRowAction.RESTORE, user) }]
                          : isSelf(user)
                            ? []
                            : [{ id: "suspend", label: t("actions.suspend"), icon: BanIcon, destructive: true, onSelect: () => onAction(UserRowAction.SUSPEND, user) }],
                      ]
                    : []
                }
                label={t("columns.actions")}
                openLabel={t("openActions")}
                sheetHeader={getUserDisplayName(user, t("unknownTeamMember"))}
              />
            )
          }
          if (!rowCanManage) {
            return <div className='text-right text-xs text-muted-foreground'>-</div>
          }

          return (
            <div className='text-right'>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant='ghost' size='icon-sm'>
                    <span className='sr-only'>{t("openActions")}</span>
                    <MoreHorizontalIcon />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align='end'>
                  <DropdownMenuLabel>{t("columns.actions")}</DropdownMenuLabel>
                  <DropdownMenuItem
                    onSelect={() => onAction(UserRowAction.EDIT_ACCESS, row.original)}
                  >
                    {t("actions.editAccess")}
                  </DropdownMenuItem>
                  {row.original.status === WorkspaceUserStatus.Suspended ? (
                    <DropdownMenuItem onSelect={() => onAction(UserRowAction.RESTORE, row.original)}>
                      {t("actions.restore")}
                    </DropdownMenuItem>
                  ) : isSelf(row.original) ? null : (
                    /* Destructive: it cuts someone off, even though it destroys nothing. */
                    <DropdownMenuItem
                      className='text-destructive focus:text-destructive'
                      onSelect={() => onAction(UserRowAction.SUSPEND, row.original)}
                    >
                      {t("actions.suspend")}
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )
        },
      },
    ],
    [canManage, canManageRow, isMobile, isSelf, onAction, t, tCommon]
  )

  const dataTableToolbar = useDataTableToolbarConfig({
    filterConfig: filterConfig,
    appliedFilters: queryState.filters,
    onApplyFilters: queryState.setFilters,
    onResetFilters: () => {
            queryState.resetFilters()
            queryState.setSearch("")
          },
    actions: toolbarActions,
    onRefresh: onRefresh,
    isRefreshing: isRefreshing,
  })

  const mobileChrome = useMobileListChrome()
  const toolbar = {
    ...dataTableToolbar,
    ...(toolbarViews ? { views: toolbarViews } : {}),
    // Phone, inside a tabbed section (My Branch): the section's title row and tabs, with ⋯ and + beside the title.
    ...(isMobile && mobileChrome ? mobileChrome : {}),
  }

  return (
    <DataTable
      data={users}
      columns={columns}
      variant={DataTableVariant.Alive}
      emptyMessage={t("empty")}
      globalFilterFn={globalFilterFn}
      globalFilter={queryState.search}
      onGlobalFilterChange={queryState.setSearch}
      initialPagination={{ pageIndex: 0, pageSize: 20 }}
      initialSorting={[{ id: "name", desc: false }]}
      initialColumnPinning={{ right: ["actions"] }}
      isLoading={isLoading}
      toolbar={toolbar}
    />
  )
}
